<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useProposalsStore } from 'dashboard/stores/proposals';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ContactProposals from 'dashboard/components-next/Contacts/ContactsSidebar/ContactProposals.vue';
import ContactProposalSummary from 'dashboard/components-next/Contacts/ContactsSidebar/ContactProposalSummary.vue';
import FollowUpSummaryCard from 'dashboard/components-next/FollowUp/FollowUpSummaryCard.vue';
import CreateProposalWizard from 'dashboard/components-next/Proposals/CreateProposalWizard.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('attributes');
const contactMergeRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const hasProposalManagement = computed(() =>
  isFeatureEnabledonAccount.value(
    route.params.accountId,
    FEATURE_FLAGS.PROPOSAL_MANAGEMENT
  )
);
const proposalWizardRef = ref(null);

// Fetched once here rather than in ContactProposalSummary and
// ContactProposals independently - both read the same Pinia store, and
// piniaGetRecords replaces store.records wholesale, so two independent
// fetches for the same contact would just be a redundant network call.
const proposalsStore = useProposalsStore();
watch(
  () => (hasProposalManagement.value ? selectedContact.value?.id : null),
  contactId => {
    if (contactId) proposalsStore.get({ contactId });
  },
  { immediate: true }
);

const CONTACT_TABS_OPTIONS = computed(() => [
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'MEDIA', value: 'media' },
  { key: 'MERGE', value: 'merge' },
  ...(hasProposalManagement.value
    ? [{ key: 'PROPOSALS', value: 'proposals' }]
    : []),
]);

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.value.map(tab => ({
    label: t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return CONTACT_TABS_OPTIONS.value.findIndex(v => v.value === activeTab.value);
});

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

// Needed for the Follow-up summary card's Assigned Agent picker — without
// this, visiting Contact Details directly (rather than via Kanban or a
// conversation, which already dispatch this) would show an empty agent list.
const fetchAgents = () => {
  store.dispatch('agents/get');
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
  fetchAgents();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ContactDetails
        v-else-if="selectedContact"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      <template #sidebarHeader>
        <div v-if="selectedContact" class="px-4 pt-4">
          <FollowUpSummaryCard
            :contact-id="selectedContact.id"
            :has-conversation="!!selectedContact?.lastConversationAt"
            :email="selectedContact.email"
            :next-follow-up-at="
              selectedContact.customAttributes?.next_follow_up_at
            "
            :follow-up-note="selectedContact.customAttributes?.follow_up_note"
            :meeting-required="
              selectedContact.customAttributes?.meeting_required
            "
            :meeting-provider="
              selectedContact.customAttributes?.meeting_provider
            "
            :meeting-url="selectedContact.customAttributes?.meeting_url"
            :meeting-reference="
              selectedContact.customAttributes?.meeting_reference
            "
            :follow-up-type="selectedContact.customAttributes?.follow_up_type"
            :meeting-location="
              selectedContact.customAttributes?.meeting_location
            "
            :follow-up-team-id="
              selectedContact.customAttributes?.follow_up_team_id
            "
            :assignee="selectedContact.assignee"
            :cc-emails="selectedContact.customAttributes?.cc_emails"
          />
          <div v-if="hasProposalManagement" class="mt-2">
            <ContactProposalSummary
              :selected-contact="selectedContact"
              @create="proposalWizardRef?.open()"
            />
          </div>
          <CreateProposalWizard
            v-if="hasProposalManagement"
            ref="proposalWizardRef"
            :contact="selectedContact"
          />
        </div>
        <div class="px-6 pt-3 pb-3">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>
      </template>
      <template #sidebar>
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else>
          <ContactCustomAttributes
            v-if="activeTab === 'attributes'"
            :selected-contact="selectedContact"
          />
          <ContactNotes v-if="activeTab === 'notes'" />
          <ContactHistory v-if="activeTab === 'history'" />
          <ContactMedia v-if="activeTab === 'media'" />
          <ContactProposals
            v-if="activeTab === 'proposals'"
            :selected-contact="selectedContact"
          />
          <ContactMerge
            v-if="activeTab === 'merge'"
            ref="contactMergeRef"
            :selected-contact="selectedContact"
            @go-to-contacts-list="goToContactsList"
            @reset-tab="handleTabChange(CONTACT_TABS_OPTIONS[0])"
          />
        </template>
      </template>
    </ContactsDetailsLayout>
  </div>
</template>
